import { useState, useEffect, useCallback } from 'react';
import {
  api,
  type MarketHistoricalTrendsResponse,
  type ZillowRentalHistoryRow,
  type RedfinMarketHistoryRow,
} from '../api/client';

export interface HistoricalDataState {
  loading: boolean;
  error: string | null;
  data: MarketHistoricalTrendsResponse | null;
  zillow: ZillowRentalHistoryRow[];
  redfin: RedfinMarketHistoryRow[];
  regionName?: string;
}

export function useHistoricalData(
  market: string | null,
  options?: {
    startDate?: string;
    endDate?: string;
    autoFetch?: boolean;
  }
): HistoricalDataState & {
  refetch: () => Promise<void>;
} {
  const { startDate, endDate, autoFetch = true } = options || {};

  const [state, setState] = useState<HistoricalDataState>({
    loading: false,
    error: null,
    data: null,
    zillow: [],
    redfin: [],
  });

  const fetchData = useCallback(async () => {
    if (!market) {
      setState(prev => ({ ...prev, loading: false, error: null, data: null, zillow: [], redfin: [] }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await api.getMarketHistoricalTrends(market, { startDate, endDate });

      setState({
        loading: false,
        error: null,
        data: response,
        zillow: response.zillow.data,
        redfin: response.redfin.data,
        regionName: response.regionName,
      });
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch historical data',
      }));
    }
  }, [market, startDate, endDate]);

  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
  }, [fetchData, autoFetch]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Helper to aggregate Zillow data by month (combining bedroom counts)
export function aggregateZillowByMonth(
  data: ZillowRentalHistoryRow[]
): Array<{
  date: string;
  medianRent: number | null;
  rentYoy: number | null;
  listings: number | null;
}> {
  // Group by period_end
  const byMonth = new Map<string, ZillowRentalHistoryRow[]>();
  for (const row of data) {
    const key = row.period_end;
    if (!byMonth.has(key)) {
      byMonth.set(key, []);
    }
    byMonth.get(key)!.push(row);
  }

  // Aggregate each month
  const result = Array.from(byMonth.entries())
    .map(([date, rows]) => {
      const rentsWithValues = rows.filter(r => r.median_asking_rent != null);
      const medianRent = rentsWithValues.length > 0
        ? Math.round(rentsWithValues.reduce((sum, r) => sum + (r.median_asking_rent || 0), 0) / rentsWithValues.length)
        : null;

      const yoyWithValues = rows.filter(r => r.median_asking_rent_yoy != null);
      const rentYoy = yoyWithValues.length > 0
        ? yoyWithValues.reduce((sum, r) => sum + (r.median_asking_rent_yoy || 0), 0) / yoyWithValues.length
        : null;

      const listings = rows.reduce((sum, r) => sum + (r.count_new_listings || 0), 0) || null;

      return { date, medianRent, rentYoy, listings };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  return result;
}

// Helper to transform Redfin data for charting
export function transformRedfinForChart(
  data: RedfinMarketHistoryRow[]
): Array<{
  date: string;
  medianSalePrice: number | null;
  activeListings: number | null;
  monthsOfSupply: number | null;
  medianDom: number | null;
}> {
  return data
    .map(row => ({
      date: row.period_start,
      medianSalePrice: row.median_sale_price || null,
      activeListings: row.active_listings || null,
      monthsOfSupply: row.months_of_supply || null,
      medianDom: row.median_dom || null,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}
